import { apiVersions, getLogicEndpoint } from '@/services/endpoints';
import { isLocalMode } from '@/services/localRuntimeClient';
import { getArmBaseUrl } from '@/services/armConfig';

// =============================================================================
// HELPERS
// =============================================================================

/** In local mode, route to mock ARM service; otherwise use the real logic endpoint. */
const getBaseUrl = (): string => (isLocalMode() ? getArmBaseUrl() : getLogicEndpoint());

/**
 * Build the environmentSettings/default resource URL. Path segments are
 * `encodeURIComponent`-escaped because project / app names can contain
 * characters that are reserved in URLs (spaces, `+`, etc.); without this,
 * the request would silently target the wrong resource.
 */
const buildSettingsUrl = (projectName: string, appName: string): string =>
  `${getBaseUrl()}/automationProjects/${encodeURIComponent(projectName)}` +
  `/applications/${encodeURIComponent(appName)}` +
  `/environmentSettings/default?api-version=${apiVersions.environmentSettings}`;

/**
 * Builds a sanitized error for API failures. We deliberately omit the
 * response body from `Error.message` because env-settings payloads can
 * echo user keys/values, and this message is later passed to
 * `telemetryService.trackException`, which does not sanitize it.
 * Structured fields (`status`, optional `requestId`) carry the diagnostic
 * signal instead.
 */
const buildApiError = (
  op: 'fetch' | 'update',
  projectName: string,
  appName: string,
  status: number,
  requestId?: string,
): ApiErrorWithStatus => {
  const verb = op === 'fetch' ? 'fetch' : 'update';
  const err: ApiErrorWithStatus = new Error(
    `Failed to ${verb} environment settings for ${projectName}/${appName}: HTTP ${status}`,
  );
  err.status = status;
  if (requestId) err.requestId = requestId;
  return err;
};

// =============================================================================
// TYPES
// =============================================================================

/**
 * Marker type for fetch-derived errors that carry the HTTP status code.
 * Consumers (hooks, telemetry) can inspect `.status` to categorize failures
 * without parsing free-form error messages.
 */
export interface ApiErrorWithStatus extends Error {
  status?: number;
  /** Server-provided request/correlation id, when available. Safe for telemetry. */
  requestId?: string;
}

export interface EnvironmentSetting {
  name: string;
  value: string | null;
  slotSetting?: boolean;
}

export interface EnvironmentSettingsResponse {
  properties: {
    settings: EnvironmentSetting[];
  };
}

export interface EnvironmentSettingsPutPayload {
  properties: {
    settings: Array<{
      name: string;
      value: string;
    }>;
  };
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Fetches environment settings for a given application.
 * Returns the array of settings from the response envelope.
 */
export const getEnvironmentSettings = async (
  projectName: string,
  appName: string,
  token: string
): Promise<EnvironmentSetting[]> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!isLocalMode()) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(buildSettingsUrl(projectName, appName), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    // Response body is intentionally NOT included in the Error to avoid
    // leaking env-var values into telemetry. See buildApiError for details.
    throw buildApiError(
      'fetch',
      projectName,
      appName,
      response.status,
      response.headers?.get('x-ms-request-id') ?? undefined,
    );
  }

  const data: EnvironmentSettingsResponse = await response.json();
  return data.properties.settings;
};

/**
 * Updates environment settings for a given application via PUT.
 *
 * **Replace semantics.** The backend uses PUT to replace the entire
 * `properties.settings` collection. Callers must therefore send the *full*
 * desired collection — settings absent from the payload are deleted. See
 * `buildPutSettings` in `@/utils/settings`.
 */
export const updateEnvironmentSettings = async (
  projectName: string,
  appName: string,
  token: string,
  settings: EnvironmentSettingsPutPayload
): Promise<EnvironmentSettingsResponse> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!isLocalMode()) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(buildSettingsUrl(projectName, appName), {
    method: 'PUT',
    headers,
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    // Response body is intentionally NOT included in the Error to avoid
    // leaking env-var values into telemetry. See buildApiError for details.
    throw buildApiError(
      'update',
      projectName,
      appName,
      response.status,
      response.headers?.get('x-ms-request-id') ?? undefined,
    );
  }

  return response.json();
};

/**
 * Merge `additions` into the current settings collection and PUT the result.
 * The backend replaces `properties.settings` wholesale, so any incremental
 * write must first read + merge to avoid clobbering unrelated keys. Existing
 * keys named in `additions` are overwritten; all others are preserved.
 */
export const upsertEnvironmentSettings = async (
  projectName: string,
  appName: string,
  token: string,
  additions: Record<string, string>,
): Promise<EnvironmentSetting[]> => {
  if (Object.keys(additions).length === 0) {
    return getEnvironmentSettings(projectName, appName, token);
  }

  const existing = await getEnvironmentSettings(projectName, appName, token);
  const merged = new Map<string, string>();
  for (const { name, value } of existing) merged.set(name, value ?? '');
  for (const [name, value] of Object.entries(additions)) merged.set(name, value);

  const response = await updateEnvironmentSettings(projectName, appName, token, {
    properties: {
      settings: Array.from(merged, ([name, value]) => ({ name, value })),
    },
  });
  return response.properties.settings;
};
